import 'pure-react-carousel/dist/react-carousel.es.css';

import _ from 'lodash';
import Image from 'next/image';
import {
  ButtonBack,
  ButtonNext,
  CarouselProvider,
  DotGroup,
  Slide,
  Slider,
} from 'pure-react-carousel';
import { useState } from 'react';

import { LikeIcon } from '@/components/common/LikeIcon/LikeIcon';
import { PackAndDiscount } from '@/components/common/PackAndDiscount/PackAndDiscount';
import { PrimaryDisplayTag } from '@/components/common/PrimaryDisplayTag/PrimaryDisplayTag';
import { SecondaryTag } from '@/components/common/SecondaryTag/SecondaryTag';
import { TertiaryTag } from '@/components/common/TertiaryTag/TertiaryTag';
import type { ProductModelType } from '@/models/api';

import { NutritionInfoContainer } from '../NutritionInfoContainer/NutritionInfoContainer';

interface ImagesSwiperTypes {
  product: ProductModelType;
}

export const ImagesSwiper: React.FC<ImagesSwiperTypes> = ({ product }) => {
  const [showButton, setShowButton] = useState(false);
  const { promotionTotal, grossPrice } = product;
  const images = product?.cms?.displayPageImages;
  const primaryDisplayTag = product?.cms?.primaryDisplayTag;
  const tertiaryDisplayTag = product.cms
    ? product.cms.tertiaryDisplayTag
    : null;
  const secondaryDisplayTag = product.cms
    ? product.cms.secondaryDisplayTag
    : null;

  const packs =
    product.hasSubProducts && product.subProducts
      ? _.sumBy(product.subProducts, (sub) => sub.qty)
      : 0;

  const mb = product.hasSubProducts ? '' : 'md:mb-10';
  if (!images) return null;
  return (
    <div className={`relative my-2 h-fit w-full rounded-lg p-0 ${mb}`}>
      <CarouselProvider
        naturalSlideWidth={100}
        naturalSlideHeight={100}
        totalSlides={images.length}
        visibleSlides={1}
        step={1}
        isIntrinsicHeight={true}
        infinite={false}
      >
        <Slider>
          {images.map((image: any, index: number) => (
            <Slide
              key={index}
              index={index}
              className="relative w-full max-w-full rounded-lg bg-white"
            >
              <LikeIcon product={product} />
              <div
                className="relative"
                onMouseEnter={() => setShowButton(true)}
                onMouseLeave={() => setShowButton(false)}
              >
                <Image
                  width={375}
                  height={375}
                  priority={true}
                  src={image.url}
                  alt="product-image"
                  className="rounded-none object-contain md:rounded-t-xl"
                  quality={100}
                  layout="responsive"
                />
              </div>
            </Slide>
          ))}
        </Slider>
        {images.length > 1 && (
          <>
            <ButtonBack
              onMouseEnter={() => setShowButton(true)}
              onMouseLeave={() => setShowButton(false)}
              className={`prc-buttonBack ${!showButton && 'hidden'}`}
            >
              <Image
                src="/assets/icons/prc-left.svg"
                alt="prc-chevron-left"
                width={48}
                height={48}
              />
            </ButtonBack>
            <ButtonNext
              className={`prc-buttonNext ${!showButton && 'hidden'}`}
              onMouseEnter={() => setShowButton(true)}
              onMouseLeave={() => setShowButton(false)}
            >
              <Image
                src="/assets/icons/prc-right.svg"
                alt="prc-chevron-right"
                width={48}
                height={48}
              />
            </ButtonNext>
            <DotGroup className="prc-dotGroup absolute inset-x-0 bottom-1/4 sm:bottom-1/4 md:bottom-1/3 lg:bottom-1/3 xl:bottom-1/4 2xl:bottom-1/4" />
          </>
        )}
        {/* Tags */}
        {primaryDisplayTag && (
          <div className="absolute left-3 right-0 top-3 md:left-4 md:top-4">
            <PrimaryDisplayTag tag={primaryDisplayTag} page={'pdp'} />
          </div>
        )}

        <div className="relative -mt-1 w-full">
          <div className="flex">
            {tertiaryDisplayTag && (
              <TertiaryTag
                tag={tertiaryDisplayTag}
                fullWidth={!secondaryDisplayTag}
                pdp={true}
              />
            )}
            {/* Secondary tags */}
            {secondaryDisplayTag && (
              <SecondaryTag
                tag={secondaryDisplayTag}
                fullWidth={!tertiaryDisplayTag}
                theme={'pdp'}
              />
            )}
          </div>
          {/* Nutrition Info */}
          {!product.hasSubProducts && (
            <NutritionInfoContainer nutritionalInfo={product.nutritionalInfo} />
          )}
          {packs > 0 && (
            <PackAndDiscount
              promotionTotal={promotionTotal}
              grossPrice={grossPrice}
              packs={packs}
              menu={true}
              size={'large'}
              quantityTag={product.cms.quantityTag}
            />
          )}
        </div>
      </CarouselProvider>
    </div>
  );
};
